import { useState } from "react";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AccountRow } from "./AccountRow";

export function EmailDialog({ user }) {
  const [open, setOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    setSending(true);
    try {
      await api.post("/me/change-email/request", { new_email: newEmail });
      setSent(true);
      toast.success("Verification link sent to your new email.");
    } catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setSent(false); setNewEmail(""); } }}>
      <DialogTrigger asChild>
        <button className="w-full" data-testid="open-email-dialog">
          <AccountRow icon={Mail} title="Email" value={user?.email || "No email on file"} testid="account-row-email" />
        </button>
      </DialogTrigger>
      <DialogContent data-testid="email-dialog">
        <DialogHeader><DialogTitle>Email Address</DialogTitle></DialogHeader>
        {sent ? (
          <div className="py-2" data-testid="email-sent-message">
            <p className="text-sm text-slate-600">We've sent a confirmation link to <strong>{newEmail}</strong>. Your email will change only after you click that link.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Current email</Label>
              <Input value={user?.email || ""} disabled data-testid="current-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label>New email</Label>
              <Input type="email" placeholder="you@example.com" data-testid="new-email-input" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
              <p className="text-xs text-slate-400">We'll email a confirmation link to the new address. Your email changes only after you confirm.</p>
            </div>
          </div>
        )}
        {!sent && (
          <DialogFooter>
            <button data-testid="email-save-btn" onClick={submit} disabled={sending || !newEmail}
              className="h-10 px-5 rounded-md bg-navy text-white text-sm font-semibold hover:bg-navy-light transition-colors disabled:opacity-60">
              {sending ? "Sending…" : "Send confirmation link"}
            </button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
